import React, { useState, useEffect, useContext, useCallback } from 'react';

import _ from 'lodash';
import { useHistory } from 'react-router';
import clsx from 'clsx';
import {
  IconButton,
  Button,
  TextField,
  Grid,
  CircularProgress,
  InputAdornment,
  Typography,
  TableRow,
  TableCell,
  Paper,
  Tooltip,
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ArrowRight as ArrowDropRightIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Visibility as VisibilityIcon,
} from '@material-ui/icons';
import InfoIcon from '@material-ui/icons/Info';
import PropTypes from 'prop-types';

// Components
import { Chip, SyncPmsConfirmDialog, TableListView } from 'components';
import ConfirmModal from 'Views/Common/ConfirmModal';
import { CategoryDiscounts } from 'Views/Plans/CreatePlan/components';
import AddPlansPopup from './AddPlansPopup';
import PmsFeeScheduleCell, {
  PmsFeeScheduleCellWrapper,
} from './PmsFeeScheduleCell';
import SyncPmsCell, { SyncPmsCellWrapper } from './SyncPmsCell';

// Constants
import { PLAN_PACKAGE_TYPE } from 'Views/Common/enum';
import { PAGE_URLS } from 'Routes/Main/constants';

// Utils
import { formatCurrency, downloadFile, sanitizeFileName } from 'utils';
import {
  getTargetValue,
  isOpenDentalVendor,
  isPmsPlanSyncEnabled,
  isSubscribiliUser,
  isSuperadminUser,
} from 'Shared/Utils';

// Services
import { LOCATION_SERVICES, PLAN_SERVICES, PMS_SERVICES } from 'Services';

// Context
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { withPlansContext } from 'Views/Plans/context';

// Styles
import styles from './AssociatedPlan.module.scss';

// Hooks
import { useCountryConfig } from 'hooks/useCountryConfig';
import useGroupUiLabel from 'hooks/useGroupUiLabel';
import useLoadGroupUiConfig from 'hooks/useLoadGroupUiConfig';

/** Values from API `pricing_tiers.pms_plan_id_source`. */
const PMS_PLAN_ID_SOURCE = {
  ADMIN: 'admin',
  PMS_SYNC: 'pms_sync',
};

const AssociatedPlan = (props) => {
  const {
    plansLoading,
    planList,
    hierarchyDetails = {},
    isCreationEnabled,
    isSettingsView,
    onUpdate,
    settings,
  } = props;
  const {
    name: level_name,
    partner_id,
    group_id,
    location_id,
    location_name,
  } = hierarchyDetails;

  const {
    state: { user },
    dispatch: globalDispatch,
  } = useContext(Context);

  useLoadGroupUiConfig(group_id);
  const upfrontFeeLabel = `Upfront/${useGroupUiLabel('activation_fee', 'Activation Fee', group_id)}`;
  const subscribiliUser = isSubscribiliUser(user);
  const superadminUser = isSuperadminUser(user);
  const isOpenDental = isOpenDentalVendor(settings);
  const { config: countryConfig } = useCountryConfig();
  const [editedRow, setEditedRow] = useState('');
  const [loadingRow, setLoadingRow] = useState('');
  const [eventsData, setEventsData] = useState([]);
  const [baseData, setBaseData] = useState([]);

  const [deletePlanId, setDeletePlanId] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [syncPlan, setSyncPlan] = useState(null);
  const [syncPlanLoading, setSyncPlanLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [groupPlans, setGroupPlans] = useState([]);
  const [downloadingFeeScheduleRow, setDownloadingFeeScheduleRow] = useState(null);

  const history = useHistory();

  /** No `pms_plan_id_source` from API (`null`), or tier row omits the field (`undefined`). */
  const isPmsPlanIdSourceNone = (source) => source == null;

  const canEditLocationPmsPlanId = (row) => {
    const source = row?.pms_plan_id_source;
    return (
      source === PMS_PLAN_ID_SOURCE.ADMIN || isPmsPlanIdSourceNone(source)
    );
  };

  const rowHasPmsPlanId = (row) => {
    const id = row?.pms_discount_id;
    return id !== null && id !== undefined && String(id).trim() !== '';
  };

  const getSyncToPmsButtonState = (row) => {
    const src = row?.pms_plan_id_source;
    // OpenDental must allow manual resync even when a PMS Plan ID already exists.
    const hiddenWhenNoPmsSync =
      !isOpenDental && isPmsPlanIdSourceNone(src) && rowHasPmsPlanId(row);

    const hidden =
      !isOpenDental &&
      (src === PMS_PLAN_ID_SOURCE.ADMIN || hiddenWhenNoPmsSync);
    const disabled =
      !isOpenDental &&
      src === PMS_PLAN_ID_SOURCE.PMS_SYNC &&
      row?.pms_plan_level === 'location';

    return { hidden, disabled };
  };

  const setGlobalNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: {
        notification: {
          severity,
          message,
        },
      },
    });

  const ensureLocationSyncPrerequisites = (planPackageId) => {
    if (!partner_id) {
      setGlobalNotification(
        'error',
        'Partner information is required to sync the plan to PMS.',
      );
      return false;
    }
    if (!location_id) {
      setGlobalNotification(
        'error',
        'Location information is required to sync the plan to PMS.',
      );
      return false;
    }
    if (!planPackageId) {
      setGlobalNotification(
        'error',
        'Plan information is required to sync the plan to PMS.',
      );
      return false;
    }
    return true;
  };

  const openAddPopup = () => setIsAddPopupOpen(true);
  const closeAddPopup = () => setIsAddPopupOpen(false);

  const handleDeleteOpen = (planId) => {
    setDeletePlanId(planId);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeletePlanId('');
    setDeleteOpen(false);
  };

  const closeSyncPlanModal = () => {
    setSyncPlan(null);
  };

  const openLocationSyncModal = (planPackageId) => {
    if (!ensureLocationSyncPrerequisites(planPackageId)) return;

    const planName =
      planList?.find((plan) => plan.plan_package_id === planPackageId)
        ?.plan_name || '';
    setSyncPlan({ planPackageId, planName });
  };

  const handleSyncPlanConfirm = async (feeExtraQuery = {}) => {
    const packageId = syncPlan?.planPackageId;
    if (syncPlanLoading) return;

    if (!ensureLocationSyncPrerequisites(packageId)) {
      closeSyncPlanModal();
      return;
    }

    try {
      setSyncPlanLoading(true);
      const extraQuery = {
        is_location_sync: 'yes',
        ...feeExtraQuery,
      };

      const response = await PMS_SERVICES.syncPlanPackage(
        packageId,
        partner_id,
        'location',
        location_id,
        extraQuery,
      );
      if (response?.type === 'success') {
        const successMessage = isOpenDental
          ? 'Plan sync triggered successfully to the PMS.'
          : 'Successfully updated the plan to PMS';
        setGlobalNotification(
          'success',
          successMessage,
        );
        onUpdate?.('Plans');
      } else {
        throw response?.message || response?.error;
      }
    } catch (err) {
      setGlobalNotification(
        'error',
        err || 'Error occured while updating the plan to PMS',
      );
    } finally {
      setSyncPlanLoading(false);
      closeSyncPlanModal();
    }
  };

  const handleConfirmDelete = async () => {
    if (!location_id) return;

    setDeleteLoading(true);

    try {
      const payload = { plan_package_ids: [deletePlanId] };
      const response = await LOCATION_SERVICES.deleteLocationPlans(
        location_id,
        payload,
      );

      if (response?.type === 'success' && response?.data) {
        setGlobalNotification(
          'success',
          'The plan has been successfully removed from this location.',
        );

        handleDeleteClose();
        onUpdate?.();
      } else {
        throw response?.message || response?.error;
      }
    } catch (err) {
      setGlobalNotification(
        'error',
        err || 'An error occurred during the deletion of the location plan.',
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const getEventsData = () => {
    let tableData = [];
    const planListCloned = _.cloneDeep(planList);
    let dataId = 0;

    planListCloned?.forEach((plan) => {
      const pTiers = plan.pricing_tiers_list || [];
      const sPlans = [];

      pTiers.forEach((pTier, pIndex) => {
        const {
          unit_amount = 0,
          upfront_amount = 0,
          signup_amount = 0,
        } = pTier?.pricing_tiers?.tiers?.[0] || {};
        const upfrontActivationFee =
          upfront_amount > 0 ? upfront_amount : signup_amount;
        sPlans.push({
          plan_id: pTier?.plan_id,
          plan_package_id: plan?.plan_package_id,
          plan_name: pIndex ? '' : plan?.plan_name,
          plan_package_type: pIndex
            ? ''
            : PLAN_PACKAGE_TYPE[plan?.plan_package_type],
          billing_frequency: pTier?.pricing_tiers?.billing_frequency,
          pms_discount_id:
            pTier?.pricing_tiers?.pms_discount_id ||
            pTier?.pricing_tiers?.pms_insurance_id ||
            '',
          pms_discount_name:
            pTier?.pricing_tiers?.pms_discount_name ||
            pTier?.pricing_tiers?.pms_insurance_name ||
            '',
          pms_fees_schedule_id: pTier?.pricing_tiers?.pms_fees_schedule_id,
          pms_plan_id_source: pTier?.pricing_tiers?.pms_plan_id_source,
          pms_plan_level: pTier?.pricing_tiers?.pms_plan_level,
          unit_amount: formatCurrency(unit_amount / 100, countryConfig),
          upfront_activation_fee: formatCurrency(upfrontActivationFee / 100, countryConfig),
          member_type: pTier?.plan_type || '-',
          status: <Chip label={pTier.status} status={pTier.status} />,
          pms_fee_schedule: pIndex ? '' : plan?.plan_package_id,
          sync_pms: pIndex ? '' : plan?.plan_package_id,
          action: pIndex ? '' : plan?.plan_package_id,
          lastRow: pIndex === pTiers.length - 1,
          dataId,
        });

        dataId++;
      });

      tableData = [...tableData, ...sPlans];
    });

    setEventsData(tableData);
    setBaseData(tableData);
  };

  const updateValues = (id, key, value) => {
    const updatedData = _.cloneDeep(eventsData);
    const updatedValue = value !== undefined ? value : baseData[id][key];
    updatedData[id] = {
      ...updatedData[id],
      [key]: updatedValue,
    };
    setEventsData(updatedData);
  };

  const handleSubmitChanges = async (index) => {
    setLoadingRow(index);

    try {
      // Normalize the free-text PMS plan name: trim, and treat a blank result
      // as "no name" so whitespace-only input is neither persisted nor shown.
      const trimmedName = (eventsData?.[index]?.pms_discount_name ?? '').trim();
      if (trimmedName !== eventsData?.[index]?.pms_discount_name) {
        updateValues(index, 'pms_discount_name', trimmedName);
      }

      const idChanged =
        eventsData?.[index]?.pms_discount_id !==
        baseData?.[index]?.pms_discount_id;
      const nameChanged =
        trimmedName !== (baseData?.[index]?.pms_discount_name ?? '');

      if (idChanged || nameChanged) {
        const payload = {};
        if (idChanged)
          payload.pms_discount_id = eventsData[index].pms_discount_id;
        if (nameChanged) payload.pms_discount_name = trimmedName;

        const plan_id = baseData[index].plan_id;
        const response = await LOCATION_SERVICES.updateLocationPlan(
          location_id,
          plan_id,
          payload,
        );
        if (response?.type === 'success') {
          setGlobalNotification(
            'success',
            'Location Plan Updated Successfully',
          );
          baseData[index].pms_discount_id = eventsData[index].pms_discount_id;
          baseData[index].pms_discount_name = trimmedName;
          setEditedRow('');
        } else {
          throw response?.message || response?.error;
        }
      } else {
        setEditedRow('');
      }
    } catch (err) {
      setGlobalNotification(
        'error',
        err || 'Error occured while updating Location Plan',
      );
    }

    setEditedRow('');
    setLoadingRow('');
  };

  const renderPmsFeeScheduleInfoIcon = (row, rowKey) => {
    const scheduleId = row?.pms_fees_schedule_id;
    if (!scheduleId) return null;

    const title = `Plan is created using fee schedule id ${scheduleId}.`;

    return (
      <Tooltip title={title}>
        <InfoIcon
          color="primary"
          fontSize="small"
          className="ml-5 cursor-pointer"
          data-testid={`pms-fee-schedule-id-info-${rowKey ?? ''}`}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
    );
  };

  const renderTableInput = (value, name, rowId) => {
    const row = eventsData?.[rowId];
    const feeScheduleInfo = renderPmsFeeScheduleInfoIcon(row, rowId);

    return (
      <Grid container alignItems="center" wrap="nowrap">
        <Grid item xs>
          <TextField
            required
            fullWidth
            variant="outlined"
            margin="dense"
            value={value}
            name={name}
            onChange={(event) =>
              updateValues(rowId, name, getTargetValue(event))
            }
            inputProps={{
              'data-testid': `pms-discount-id-edit-${rowId}`,
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^A-Za-z0-9_-]/g, '');
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  className={clsx(
                    styles.actionIcon,
                    'theme_color_orange cursor_pointer',
                  )}
                >
                  <>
                    {loadingRow === rowId ? (
                      <CircularProgress thickness={5} size={20} />
                    ) : (
                      <>
                        <SaveIcon
                          fontSize="small"
                          className="mr-10"
                          onClick={() => handleSubmitChanges(rowId)}
                          data-testid={`pms-id-save-icon-${rowId}`}
                        />
                        <CloseIcon
                          fontSize="small"
                          onClick={() => {
                            setEditedRow('');
                            updateValues(rowId, 'pms_discount_id');
                          }}
                          data-testid={`pms-id-cancel-icon-${rowId}`}
                        />
                      </>
                    )}
                  </>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {feeScheduleInfo && (
          <Grid item className="d-flex align-center" style={{ marginLeft: 8 }}>
            {feeScheduleInfo}
          </Grid>
        )}
      </Grid>
    );
  };

  const handleDownloadFeeSchedule = useCallback(
    async (planPackageId, rowIndex) => {
      if (!location_id || !planPackageId) return;

      setDownloadingFeeScheduleRow(planPackageId);
      try {
        const response = await LOCATION_SERVICES.getLocationPlanPackageFeeSchedule(
          location_id,
          planPackageId,
        );
        if (response?.type === 'success' && response?.data) {
          const planNameRaw = eventsData?.[rowIndex]?.plan_name || 'Plan_name';
          const locationNameRaw = location_name || 'Location_name';
          
          const planName = sanitizeFileName(planNameRaw);
          const locationName = sanitizeFileName(locationNameRaw);
          
          const feeScheduleFileName = `${planName}_${locationName}_Fee_Schedule.csv`;
          
          downloadFile(response.data, 'text/csv', feeScheduleFileName);
          setGlobalNotification('success', 'Fee schedule downloaded successfully.');
        } else {
          throw response?.message || response?.error;
        }
      } catch (err) {
        setGlobalNotification(
          'error',
          err || 'An error occurred while downloading the fee schedule.',
        );
      } finally {
        setDownloadingFeeScheduleRow(null);
      }
    },
    [eventsData, location_id, location_name, setGlobalNotification]
  );

  const renderTableValue = (value, rowIndex) => {
    const row = eventsData?.[rowIndex];

    if (value)
      return (
        <Grid item className="d-flex align-center">
          {value}
          {canEditLocationPmsPlanId(row) && (
            <EditIcon
              className="theme_color_orange"
              fontSize="small"
              style={{ marginLeft: 8 }}
              data-testid={`${rowIndex}-edit-pms-id`}
              onClick={(e) => {
                e.stopPropagation();

                setEditedRow(rowIndex);
              }}
            />
          )}
          {renderPmsFeeScheduleInfoIcon(row, rowIndex)}
        </Grid>
      );

    return (
      <Grid item className="d-flex align-center">
        <Typography
          className="theme_color_orange"
          fontSize="small"
          data-testid={`${rowIndex}-add-pms-id`}
          style={
            canEditLocationPmsPlanId(row) ? { textDecoration: 'underline' } : {}
          }
          onClick={(e) => {
            e.stopPropagation();
            if (!canEditLocationPmsPlanId(row)) return;
            setEditedRow(rowIndex);
          }}
        >
          Missing Plan Id
        </Typography>
        {renderPmsFeeScheduleInfoIcon(row, rowIndex)}
      </Grid>
    );
  };

  const renderPmsPlanNameInput = (value, rowId) => (
    <Grid container alignItems="center" wrap="nowrap">
      <Grid item xs>
        <TextField
          required
          fullWidth
          variant="outlined"
          margin="dense"
          value={value}
          name="pms_discount_name"
          onChange={(event) =>
            updateValues(rowId, 'pms_discount_name', getTargetValue(event))
          }
          inputProps={{
            'data-testid': `pms-plan-name-edit-${rowId}`,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                className={clsx(
                  styles.actionIcon,
                  'theme_color_orange cursor_pointer',
                )}
              >
                <>
                  {loadingRow === rowId ? (
                    <CircularProgress thickness={5} size={20} />
                  ) : (
                    <>
                      <SaveIcon
                        fontSize="small"
                        className="mr-10"
                        onClick={() => handleSubmitChanges(rowId)}
                        data-testid={`pms-plan-name-save-icon-${rowId}`}
                      />
                      <CloseIcon
                        fontSize="small"
                        onClick={() => {
                          setEditedRow('');
                          updateValues(rowId, 'pms_discount_name');
                        }}
                        data-testid={`pms-plan-name-cancel-icon-${rowId}`}
                      />
                    </>
                  )}
                </>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </Grid>
  );

  const renderPmsPlanNameValue = (value, rowIndex) => {
    const row = eventsData?.[rowIndex];

    if (value)
      return (
        <Grid item className="d-flex align-center">
          {value}
          {canEditLocationPmsPlanId(row) && (
            <EditIcon
              className="theme_color_orange"
              fontSize="small"
              style={{ marginLeft: 8 }}
              data-testid={`${rowIndex}-edit-pms-name`}
              onClick={(e) => {
                e.stopPropagation();

                setEditedRow(rowIndex);
              }}
            />
          )}
        </Grid>
      );

    return (
      <Grid item className="d-flex align-center">
        <Typography
          className="theme_color_orange"
          fontSize="small"
          data-testid={`${rowIndex}-add-pms-name`}
          style={
            canEditLocationPmsPlanId(row) ? { textDecoration: 'underline' } : {}
          }
          onClick={(e) => {
            e.stopPropagation();
            if (!canEditLocationPmsPlanId(row)) return;
            setEditedRow(rowIndex);
          }}
        >
          Missing Plan Name
        </Typography>
      </Grid>
    );
  };

  const renderColumnLabelWithInfo = (label, title, testId) => (
    <Grid container alignItems="center" wrap="nowrap" component="span">
      {label}
      <Tooltip title={title}>
        <InfoIcon
          color="primary"
          fontSize="small"
          className="ml-5 cursor-pointer"
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
    </Grid>
  );

  const renderActionButtons = (rowIndex, planId) => {
    const viewButton = (
      <IconButton
        aria-label="view"
        size="small"
        onClick={(e) => {
          e.stopPropagation();

          viewPlan(planId);
        }}
        data-testid={`view-plan-button-${rowIndex}`}
      >
        <VisibilityIcon className="theme_color_orange" fontSize="inherit" />
      </IconButton>
    );

    const deleteButton = (
      <IconButton
        aria-label="delete"
        size="small"
        onClick={(e) => {
          e.stopPropagation();

          handleDeleteOpen(planId);
        }}
        data-testid={`delete-plan-button-${rowIndex}`}
      >
        <DeleteIcon className="theme_color_orange" fontSize="inherit" />
      </IconButton>
    );

    return <>{level_name === 'Location' ? deleteButton : viewButton}</>;
  };

  const commonCellProps = () => ({ className: styles.tableColumn });
  const pmsCellProps = () => ({ className: styles.pmsColumn });

  const renderPmsFeeScheduleCell = useCallback(
    (value, rowIndex) => (
      <PmsFeeScheduleCell
        planPackageId={value}
        rowIndex={rowIndex}
        isDownloading={downloadingFeeScheduleRow === value}
        onDownload={handleDownloadFeeSchedule}
        downloadLinkClassName={styles.downloadLink}
      />
    ),
    [downloadingFeeScheduleRow, handleDownloadFeeSchedule],
  );

  const renderSyncPmsCell = useCallback(
    (planPackageId, rowIndex) => {
      const row = eventsData?.[rowIndex];
      const { hidden, disabled } = getSyncToPmsButtonState(row);
      return (
        <SyncPmsCell
          planPackageId={planPackageId}
          rowIndex={rowIndex}
          hidden={hidden}
          disabled={disabled}
          onOpenSyncModal={openLocationSyncModal}
        />
      );
    },
    [eventsData, openLocationSyncModal],
  );

  const renderPlanName = (value, tableMetaData) => {
    if (!value) return <></>;

    const { rowIndex } = tableMetaData;

    const packageId = eventsData?.[rowIndex]?.plan_package_id;
    const { dataId = 0 } =
      eventsData?.find(
        (indData) => indData?.plan_package_id === packageId && indData?.lastRow,
      ) || {};

    return (
      <Grid item className="d-flex align-center">
        {level_name === 'Location' &&
          (expandedRows.includes(dataId) ? (
            <ArrowDropDownIcon
              className="mr-10 cursor_pointer"
              onClick={() => handleOpenClick(rowIndex, dataId)}
            />
          ) : (
            <ArrowDropRightIcon
              className="mr-10 cursor_pointer"
              onClick={() => handleOpenClick(rowIndex, dataId)}
            />
          ))}
        {value}
      </Grid>
    );
  };

  const showLocationPmsColumns = level_name === 'Location';
  const showSyncPmsColumn =
    showLocationPmsColumns &&
    (superadminUser || (isOpenDental && subscribiliUser)) &&
    isPmsPlanSyncEnabled(settings);

  const tableColumns = [
    {
      name: 'plan_name',
      label: 'Plan Name',
      options: {
        setCellProps: commonCellProps,
        customBodyRender: renderPlanName,
      },
    },
    {
      name: 'plan_package_type',
      label: 'Plan Type',
      options: {
        setCellProps: commonCellProps,
      },
    },
    {
      name: 'member_type',
      label: 'Member Type',
      options: {
        setCellProps: commonCellProps,
      },
    },
    {
      name: 'unit_amount',
      label: 'Unit Price',
      options: {
        setCellProps: commonCellProps,
      },
    },
    {
      name: 'upfront_activation_fee',
      label: upfrontFeeLabel,
      options: {
        setCellProps: commonCellProps,
      },
    },
    {
      name: 'billing_frequency',
      label: 'Billing Frequency',
      options: {
        setCellProps: commonCellProps,
      },
    },
    {
      name: 'status',
      label: 'Plan Status',
      options: {
        setCellProps: commonCellProps,
      },
    },
    {
      name: 'pms_discount_id',
      label: 'PMS Plan ID',
      options: {
        setCellProps: pmsCellProps,
        display: level_name === 'Location' ? true : 'excluded',
        customHeadLabelRender: () =>
          renderColumnLabelWithInfo(
            'PMS Plan ID',
            "Identifies this plan in your PMS for assigning subscriptions (used by most PMS). Saved as the insurance or fee-schedule ID, per the plan's mode.",
            'pms-plan-id-header-info',
          ),
        customBodyRender: (value, tableMetaData) => {
          const { rowIndex } = tableMetaData;

          const row = eventsData?.[rowIndex];

          return editedRow === rowIndex && canEditLocationPmsPlanId(row)
            ? renderTableInput(value, 'pms_discount_id', rowIndex)
            : renderTableValue(value, rowIndex);
        },
      },
    },
    {
      name: 'pms_discount_name',
      label: 'PMS Plan Name',
      options: {
        setCellProps: pmsCellProps,
        display: level_name === 'Location' ? true : 'excluded',
        customHeadLabelRender: () =>
          renderColumnLabelWithInfo(
            'PMS Plan Name',
            "Only used for Denticon fee-schedule plans, which are assigned by name. Insurance-mode plans and other PMS are assigned by the ID. Saved as the insurance or fee-schedule name, per the plan's mode.",
            'pms-plan-name-header-info',
          ),
        customBodyRender: (value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          const row = eventsData?.[rowIndex];
          return editedRow === rowIndex && canEditLocationPmsPlanId(row)
            ? renderPmsPlanNameInput(value, rowIndex)
            : renderPmsPlanNameValue(value, rowIndex);
        },
      },
    },
    {
      name: 'sync_pms',
      label: 'Sync to PMS',
      options: {
        setCellProps: commonCellProps,
        sort: false,
        display: showSyncPmsColumn ? true : 'excluded',
        customBodyRender: (planPackageId, tableMetaData) => (
          <SyncPmsCellWrapper
            renderSyncPmsCell={renderSyncPmsCell}
            planPackageId={planPackageId}
            rowIndex={tableMetaData.rowIndex}
          />
        ),
      },
    },
    {
      name: 'pms_fee_schedule',
      label: 'PMS Fee Schedule',
      options: {
        setCellProps: commonCellProps,
        display: level_name === 'Location' ? true : 'excluded',
        customBodyRender: (value, tableMetaData) => (
          <PmsFeeScheduleCellWrapper
            renderPmsFeeScheduleCell={renderPmsFeeScheduleCell}
            value={value}
            rowIndex={tableMetaData.rowIndex}
          />
        ),
      },
    },
    {
      name: 'action',
      label: 'Action',
      options: {
        setCellProps: commonCellProps,
        sort: false,
        display: !isSettingsView && isCreationEnabled,
        customBodyRender: (planId, tableMetaData) => {
          const { rowIndex } = tableMetaData;

          if (!planId) return '';

          return (
            <Grid container spacing={2}>
              <Grid item alignItems="center" justifyContent="center">
                {renderActionButtons(rowIndex, planId)}
              </Grid>
            </Grid>
          );
        },
      },
    },
  ];

  const getPlansByGroupid = async () => {
    if (!group_id) return;

    try {
      const res = await PLAN_SERVICES.fetchGroupPlanPackages(group_id);

      if (res?.type === 'success' && res?.data) {
        setGroupPlans(res?.data?.rows);
      } else {
        throw res;
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (level_name === 'Location') getPlansByGroupid();
  }, [group_id]);

  useEffect(() => {
    getEventsData();
  }, [planList]);

  const triggerPlanCreation = () =>
    history.push({
      pathname: PAGE_URLS.PLANS_CREATE,
      state: { partner_id, group_id },
    });

  const viewPlan = (planPackageId) => {
    if (!planPackageId) return;

    history.push({
      pathname: PAGE_URLS.PLANS_INFO,
      state: {
        plan_package_id: planPackageId,
        group_id,
      },
    });
  };

  const handleOpenClick = (rowIndex, dataId) => {
    if (isSettingsView || !(isCreationEnabled || subscribiliUser)) return;

    if (editedRow !== '') {
      updateValues(rowIndex, 'pms_discount_id');
    }

    if (expandedRows.includes(dataId)) setExpandedRows([]);
    else setExpandedRows([dataId]);
  };

  const btnProps = {
    otherButtons:
      isCreationEnabled &&
      !subscribiliUser &&
      (level_name === 'Group' ||
        (groupPlans.length && groupPlans.length !== planList.length)) ? (
        <Button
          onClick={() =>
            level_name === 'Group' ? triggerPlanCreation() : openAddPopup()
          }
          color="secondary"
          size="large"
          data-testid="add-plan"
        >
          + Add Plan
        </Button>
      ) : (
        ''
      ),
  };

  const otherButtons = (
    <Button
      onClick={() => setExpandedRows([])}
      color="secondary"
      variant="outlined"
      size="large"
      data-testid="category-discount-cancel-button"
      className={styles.expandRowsCancelBtn}
    >
      Cancel
    </Button>
  );

  const renderExpandableRows = (_rowData, { rowIndex }) => {
    const planPackageId = eventsData?.[rowIndex]?.plan_package_id;
    // Base columns in `tableColumns` before optional PMS/sync/action columns below.
    const baseColumnCount = 7;
    const expandRowColSpan =
      baseColumnCount +
      (showLocationPmsColumns ? 3 : 0) +
      (showSyncPmsColumn ? 1 : 0) +
      (!isSettingsView && isCreationEnabled ? 1 : 0);

    return (
      <TableRow>
        <TableCell colSpan={expandRowColSpan}>
          <Paper variant="outlined" elevation={0} className="widget-wrapper">
            <CategoryDiscounts
              level={'location'}
              levelId={location_id}
              planPackageId={planPackageId}
              otherButtons={otherButtons}
            />
          </Paper>
        </TableCell>
      </TableRow>
    );
  };

  const handleChangeRowsPerPage = (updatedRowsPerPage) =>
    setRowsPerPage(updatedRowsPerPage);

  const tableOptions = {
    sort: false,
    rowsPerPage,
    rowsPerPageOptions: [10, 25, 100],
    onChangeRowsPerPage: handleChangeRowsPerPage,
    expandableRows: level_name === 'Location',
    rowsExpanded: expandedRows,
    renderExpandableRow: renderExpandableRows,
  };

  return (
    <>
      <TableListView
        headerTitle="Associated Plan(s)"
        tableData={eventsData}
        tableOptions={tableOptions}
        tableColumns={tableColumns}
        pagination={Boolean(eventsData.length > 10)}
        headerSection={true}
        emptyPlaceHolderContent="No Associated Plans found"
        className={'location-plans-table'}
        {...btnProps}
        tableProps={{
          components: {
            ExpandButton: () => <></>,
          },
        }}
        isLoading={plansLoading}
      />

      {deleteOpen && (
        <ConfirmModal
          isOpen={true}
          closeModal={handleDeleteClose}
          emitConfirmEvent={handleConfirmDelete}
          isLoading={deleteLoading}
          options={{
            title: `Delete Plan`,
            description: (
              <>
                Are you sure you want to delete the plan <br />
                from this location?
              </>
            ),
            confirmText: 'Confirm',
            cancelText: 'Cancel',
          }}
        />
      )}

      {isAddPopupOpen && (
        <AddPlansPopup
          data={hierarchyDetails}
          onClose={closeAddPopup}
          onUpdate={onUpdate}
          groupPlans={groupPlans}
          locationPlans={planList}
        />
      )}

      {syncPlan?.planPackageId && (
        <SyncPmsConfirmDialog
          open={Boolean(syncPlan?.planPackageId)}
          onClose={closeSyncPlanModal}
          onConfirm={handleSyncPlanConfirm}
          isLoading={syncPlanLoading}
          planName={syncPlan.planName}
          locationName={location_name}
          forceUcrFeeSchedule={isOpenDental}
        />
      )}
    </>
  );
};

AssociatedPlan.propTypes = {
  plansLoading: PropTypes.bool,
  planList: PropTypes.array.isRequired,
  hierarchyDetails: PropTypes.object.isRequired,
  isCreationEnabled: PropTypes.bool,
  isSettingsView: PropTypes.bool,
  onUpdate: PropTypes.func,
  settings: PropTypes.object,
};

export default withPlansContext(AssociatedPlan);
